import React, { FC, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAddTeam } from '../../../hooks/useAddTeam';
import { DepartmentsEntity } from '../../../hooks/useFulfillDepartments';
import { IParticipantsItem } from '../../../services/surveys';
import SearchDropdown from '../../SearchDropdown/SearchDropdown';
import SearchInputHeader from '../../SearchDropdown/SearchInputHeader/SearchInputHeader';
import { IPostParticipantBodyPreTransform } from '../AddParticipantModal';
import {
  AdditionalLabel,
  AddParticipantFormCont,
  ControllerCont,
  FormPartCell,
  Label,
} from './AddParticipantForm.styles';
import participantSchemaBuilder from '@/helpers/participantSchemaBuilder';

const respondentsDefault = () => [
  {
    id: 'member',
    name: `${t('viewSurvey.reportsSetup.participants.additional.member')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.memberDescription')}`,
  },
  {
    id: 'leader',
    name: `${t('viewSurvey.reportsSetup.participants.additional.leader')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.leaderDescription')}`,
  },
  {
    id: 'superordinate',
    name: `${t('viewSurvey.reportsSetup.participants.additional.superordinate')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.superordinateDescription')}`,
  },
  {
    id: 'subordinate',
    name: `${t('viewSurvey.reportsSetup.participants.additional.subordinate')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.subordinateDescription')}`,
  },
];

export interface ReconfiguredDepartmentEntity {
  id: string;
  name: string;
}

interface IAddParticipantForm {
  submit: (data: IPostParticipantBodyPreTransform) => void;
  baseLang: string;
  apiError?: { [key: string]: string };
  editData?: IParticipantsItem;
  surveyType: string;
  departments: DepartmentsEntity[];
}

const AddParticipantForm: FC<IAddParticipantForm> = ({
  submit,
  baseLang,
  apiError,
  editData,
  surveyType,
  departments,
}) => {
  const isEffectSurvey = surveyType === 'EFFECT';
  const userLang = baseLang || 'en';
  const { langArrWithIcons, getLangObjectShort } = useAddTeam();
  const { t } = useTranslation();

  const { defaultValues, defaultSchema } = participantSchemaBuilder(
    isEffectSurvey,
    editData,
    getLangObjectShort,
    userLang,
    departments
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IPostParticipantBodyPreTransform>({
    defaultValues,
    resolver: yupResolver(defaultSchema as any),
  });

  const inputRef = useRef(null);
  return (
    <AddParticipantFormCont onSubmit={handleSubmit(submit)}>
      <FormPartCell apiError={!!errors?.name?.message || !!apiError?.name}>
        <Label>{t('viewSurvey.participants.form.name')}</Label>
        <ControllerCont>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <SearchInputHeader
                width="calc(100% - 16px)"
                noDrop
                searchForList={onChange}
                active={false}
                placeholder={t('viewSurvey.participants.membersForm.name')}
                inputRef={inputRef}
                defaultValue={value}
                error={errors?.name?.message || apiError?.name}
              />
            )}
            name="name"
          />
        </ControllerCont>
      </FormPartCell>
      <FormPartCell
        apiError={!!errors?.emailAddress?.message || !!apiError?.emailAddress}
        type="email"
      >
        <Label>{t('viewSurvey.participants.membersForm.email')}</Label>
        <ControllerCont>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <SearchInputHeader
                width="calc(100% - 16px)"
                noDrop
                searchForList={onChange}
                active={false}
                placeholder={t('viewSurvey.participants.membersForm.email')}
                inputRef={inputRef}
                defaultValue={value}
                error={errors?.emailAddress?.message || apiError?.emailAddress}
              />
            )}
            name="emailAddress"
          />
        </ControllerCont>
      </FormPartCell>

      <FormPartCell apiError={!!errors?.phoneNo?.message || !!apiError?.phoneNo}>
        <Label>{t('viewSurvey.participants.form.phone')}</Label>
        <AdditionalLabel>{t('viewSurvey.participants.form.optional')}</AdditionalLabel>
        <ControllerCont>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <SearchInputHeader
                width="calc(100% - 16px)"
                noDrop
                searchForList={onChange}
                active={false}
                placeholder="(+__) __ - __ - __"
                inputRef={inputRef}
                defaultValue={value}
                error={errors?.phoneNo?.message || apiError?.phoneNo}
              />
            )}
            name="phoneNo"
          />
        </ControllerCont>
      </FormPartCell>
      <FormPartCell>
        <Label>{t('viewSurvey.participants.form.lang')}</Label>
        <Controller
          control={control}
          render={({ field: { onChange, value } }) => (
            <SearchDropdown
              items={langArrWithIcons}
              placeholder={t('viewSurvey.participants.form.country')}
              selectEl={onChange}
              width="303px"
              activeEl={value}
              selectFullObject
            />
          )}
          name="preferredLanguage"
        />
      </FormPartCell>

      {isEffectSurvey ? (
        <>
          <FormPartCell apiError={!!errors?.role?.message || !!apiError?.role}>
            <Label>{t('viewSurvey.participants.form.respondents')}</Label>
            <ControllerCont>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SearchDropdown
                    items={respondentsDefault()}
                    placeholder={t('viewSurvey.participants.form.respondents')}
                    selectEl={selectedId => {
                      const selectedItem = respondentsDefault().find(
                        item => item.name === selectedId
                      );

                      if (selectedItem) {
                        onChange(selectedItem.id);
                      }
                    }}
                    width="303px"
                    activeEl={respondentsDefault().find(item => item.id === value)?.name}
                    selectFullObject={false}
                    error={errors?.role?.message || apiError?.role}
                  />
                )}
                name="role"
              />
            </ControllerCont>
          </FormPartCell>
          <FormPartCell apiError={!!errors?.department?.message || !!apiError?.department}>
            <Label>{t('viewSurvey.participants.form.departments')}</Label>
            <ControllerCont>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SearchDropdown
                    items={departments.map(({ id, name }) => ({ id, name }))}
                    placeholder={t('viewSurvey.participants.form.departments')}
                    selectEl={onChange}
                    width="303px"
                    activeEl={value}
                    selectFullObject
                    error={
                      errors?.department?.id?.message ||
                      (apiError?.department &&
                        t('viewSurvey.participants.modals.validations.departments'))
                    }
                  />
                )}
                name="department"
              />
            </ControllerCont>
          </FormPartCell>
        </>
      ) : (
        <>
          <FormPartCell apiError={!!errors?.team?.message || !!apiError?.team}>
            <Label>{t('viewSurvey.participants.form.team')}</Label>
            <ControllerCont>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SearchInputHeader
                    width="calc(100% - 16px)"
                    noDrop
                    searchForList={onChange}
                    active={false}
                    placeholder="Team"
                    inputRef={inputRef}
                    defaultValue={value}
                    error={errors?.team?.message || apiError?.team}
                  />
                )}
                name="team"
              />
            </ControllerCont>
          </FormPartCell>
          <FormPartCell apiError={!!errors?.leader?.message || !!apiError?.leader}>
            <Label>{t('viewSurvey.participants.form.leader')}</Label>
            <ControllerCont>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SearchInputHeader
                    width="calc(100% - 16px)"
                    noDrop
                    searchForList={onChange}
                    active={false}
                    placeholder={t('viewSurvey.participants.form.leader')}
                    inputRef={inputRef}
                    defaultValue={value}
                    error={errors?.leader?.message || apiError?.leader}
                  />
                )}
                name="leader"
              />
            </ControllerCont>
          </FormPartCell>
        </>
      )}

      <button
        data-testid="submitAddParticipant"
        id="AddParticipantModalCallToAction"
        type="submit"
        hidden
      >
        callToAction
      </button>
    </AddParticipantFormCont>
  );
};

export default AddParticipantForm;
